import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet's default marker icons under Vite bundling (same as MapPicker).
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

/**
 * HotelLocationMap — a focused, read-only Leaflet/OpenStreetMap map for the
 * hotel-details view. Plots the HOTEL (purple pin) plus the trip's golf COURSES
 * (green = connected/bookable, amber = request) so the reseller can see where
 * the hotel sits relative to the golf. Fits the view to all markers.
 *
 * Props:
 *   hotel:   { lat, lng, name }
 *   courses: [{ lat, lng, name, connected }]
 *   height:  px (default 340)
 */
export default function HotelLocationMap({ hotel, courses = [], height = 340 }) {
  const elRef = useRef(null)
  const mapRef = useRef(null)
  const layerRef = useRef(null)

  // Init map once.
  useEffect(() => {
    if (!elRef.current || mapRef.current) return
    const map = L.map(elRef.current, { zoomControl: true, scrollWheelZoom: true })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map
    requestAnimationFrame(() => { try { map.invalidateSize() } catch { /* noop */ } })
    return () => { try { map.remove() } catch { /* noop */ } mapRef.current = null }
  }, [])

  // (Re)draw markers whenever the hotel or courses change (courses arrive async
  // with the detail fetch, after the map has already mounted).
  useEffect(() => {
    const map = mapRef.current, grp = layerRef.current
    if (!map || !grp) return
    grp.clearLayers()
    const pts = []

    const hLat = Number(hotel?.lat), hLng = Number(hotel?.lng)
    if (Number.isFinite(hLat) && Number.isFinite(hLng)) {
      const html = `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
        background:#7c3aed;border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,0.4);
        display:flex;align-items:center;justify-content:center;">
        <span style="transform:rotate(45deg);font-size:14px;">🏨</span></div>`
      L.marker([hLat, hLng], {
        icon: L.divIcon({ className: '', html, iconSize: [30, 30], iconAnchor: [15, 30] }),
        zIndexOffset: 1000,
      }).addTo(grp).bindTooltip(hotel?.name || 'Hotel', { direction: 'top', offset: [0, -28] })
      pts.push([hLat, hLng])
    }

    for (const c of (courses || [])) {
      const clat = Number(c?.lat), clng = Number(c?.lng)
      if (!Number.isFinite(clat) || !Number.isFinite(clng)) continue
      const fill = c.connected ? '#16a34a' : '#f59e0b'
      L.circleMarker([clat, clng], { radius: 8, color: '#ffffff', weight: 2, fillColor: fill, fillOpacity: 1 })
        .addTo(grp).bindTooltip('⛳ ' + (c.name || 'Course'), { direction: 'top', offset: [0, -6] })
      pts.push([clat, clng])
    }

    if (pts.length > 1) { try { map.fitBounds(pts, { padding: [36, 36], maxZoom: 14 }) } catch { map.setView(pts[0], 12) } }
    else if (pts.length === 1) map.setView(pts[0], 14)
    requestAnimationFrame(() => { try { map.invalidateSize() } catch { /* noop */ } })
  }, [hotel?.lat, hotel?.lng, hotel?.name, courses])

  return <div ref={elRef} style={{ height, width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }} />
}
